import { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthentication from "../../hooks/useAuthentication";
import {
  addEducation,
  updateEducation,
  deleteEducation,
  searchInstitutions,
} from "../../utility/httpActions/userActions";

const EDUCATION_LEVELS = [
  { value: "PRIMARY", label: "Primary School" },
  { value: "JUNIOR_SECONDARY", label: "Junior Secondary / Middle School" },
  { value: "SENIOR_SECONDARY", label: "Senior Secondary / High School" },
  { value: "VOCATIONAL", label: "Vocational / Technical Training" },
  { value: "DIPLOMA", label: "Diploma / OND" },
  { value: "NCE", label: "NCE (Nigerian Certificate in Education)" },
  { value: "ASSOCIATE", label: "Associate Degree" },
  { value: "HND", label: "Higher National Diploma (HND)" },
  { value: "BACHELORS", label: "Bachelor's Degree" },
  { value: "MASTERS", label: "Master's Degree" },
  { value: "MBA", label: "MBA" },
  { value: "PHD", label: "PhD / Doctorate" },
  { value: "PROFESSIONAL", label: "Professional Degree (JD, MD, etc.)" },
  { value: "CERTIFICATION", label: "Professional Certification" },
  { value: "APPRENTICESHIP", label: "Apprenticeship / Fellowship" },
  { value: "BOOTCAMP", label: "Bootcamp / Intensive Program" },
];

const LEVEL_LABELS = new Map(EDUCATION_LEVELS.map((l) => [l.value, l.label]));

const EducationDialog = ({ open, onClose, education = null }) => {
  const isEdit = !!education;
  const queryClient = useQueryClient();
  const { setCurrentUser } = useAuthentication();

  const [level, setLevel] = useState(education?.level || "");
  const [institution, setInstitution] = useState(education?.institution || "");
  const [fieldOfStudy, setFieldOfStudy] = useState(education?.fieldOfStudy || "");
  const [startYear, setStartYear] = useState(education?.startYear?.toString() || "");
  const [endYear, setEndYear] = useState(education?.endYear?.toString() || "");
  const [isCurrent, setIsCurrent] = useState(education?.isCurrent || false);
  const [description, setDescription] = useState(education?.description || "");
  const [snackbar, setSnackbar] = useState(null);

  const [instOptions, setInstOptions] = useState([]);
  const [instLoading, setInstLoading] = useState(false);

  useEffect(() => {
    if (institution.length < 2) { setInstOptions([]); return; }
    const timer = setTimeout(async () => {
      setInstLoading(true);
      try {
        const results = await searchInstitutions({ q: institution, type: "education" });
        setInstOptions(results.map((r) => r.label));
      } catch {
        setInstOptions([]);
      }
      setInstLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [institution]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    queryClient.invalidateQueries({ queryKey: ["publicUser"] });
  };

  const onSuccess = (data) => {
    if (data?.user) setCurrentUser(data.user);
    invalidate();
    onClose();
  };

  const onError = (err) => {
    setSnackbar({ severity: "error", message: err?.data?.message || "Something went wrong." });
  };

  const { mutate: doAdd, isPending: adding } = useMutation({ mutationFn: addEducation, onSuccess, onError });
  const { mutate: doUpdate, isPending: updating } = useMutation({ mutationFn: updateEducation, onSuccess, onError });
  const { mutate: doDelete, isPending: deleting } = useMutation({ mutationFn: deleteEducation, onSuccess, onError });

  const isSaving = adding || updating || deleting;

  const handleSave = () => {
    const payload = {
      level,
      institution: institution.trim(),
      fieldOfStudy: fieldOfStudy.trim() || undefined,
      startYear: parseInt(startYear, 10),
      endYear: isCurrent ? null : (endYear ? parseInt(endYear, 10) : null),
      isCurrent,
      description: description.trim() || undefined,
    };
    if (isEdit) {
      doUpdate({ educationId: education._id || education.id, ...payload });
    } else {
      doAdd(payload);
    }
  };

  const handleDelete = () => {
    if (window.confirm("Delete this education entry?")) {
      doDelete(education._id || education.id);
    }
  };

  const valid = level && institution.trim() && startYear && !isNaN(parseInt(startYear, 10));

  return (
    <>
      <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? "Edit education" : "Add education"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField
            select
            label="Level"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            required
            fullWidth
            size="small"
            disabled={isSaving}
          >
            {EDUCATION_LEVELS.map((l) => (
              <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
            ))}
          </TextField>
          <Autocomplete
            freeSolo
            options={instOptions}
            loading={instLoading}
            inputValue={institution}
            onInputChange={(_, val) => setInstitution(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Institution"
                required
                size="small"
                disabled={isSaving}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {instLoading && <CircularProgress size={16} sx={{ color: "#9ca3af" }} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Field of study (optional)"
            value={fieldOfStudy}
            onChange={(e) => setFieldOfStudy(e.target.value)}
            fullWidth
            size="small"
            disabled={isSaving}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Start year"
              value={startYear}
              onChange={(e) => setStartYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              required
              size="small"
              sx={{ flex: 1 }}
              disabled={isSaving}
              inputProps={{ inputMode: "numeric" }}
            />
            <TextField
              label="End year"
              value={isCurrent ? "" : endYear}
              onChange={(e) => setEndYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              size="small"
              sx={{ flex: 1 }}
              disabled={isSaving || isCurrent}
              inputProps={{ inputMode: "numeric" }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCurrent}
                onChange={(e) => setIsCurrent(e.target.checked)}
                size="small"
                sx={{ "&.Mui-checked": { color: "#046287" } }}
              />
            }
            label={<Typography variant="body2">I currently attend here</Typography>}
            disabled={isSaving}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: isEdit ? "space-between" : "flex-end" }}>
          {isEdit && (
            <Button color="error" onClick={handleDelete} disabled={isSaving}>
              Delete
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!valid || isSaving}
              sx={{ bgcolor: "#046287", "&:hover": { bgcolor: "#035070" } }}
            >
              {isSaving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Save"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export { LEVEL_LABELS };
export default EducationDialog;
